import { useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { CircleArrowLeft, Loader2, RefreshCw } from 'lucide-react';

import { useGetStudioPreflightCheck } from '~entities/studios/api/get-preflight-check';
import { useStudioStore } from '~entities/studios/model/studio-store';

import { Alert, AlertDescription, AlertTitle } from '~shared/shadcn/ui/alert';
import { Button } from '~shared/shadcn/ui/button';

import { getVisiblePreflightSummary } from './preflight-check.helpers';
import { PreflightCheckCategory } from './ui/preflight-check-category';

export const PreflightCheckPage = () => {
  const currentStudioId = useStudioStore((state) => state.currentStudioId);
  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useGetStudioPreflightCheck();

  const isStudioMismatch =
    Boolean(data?.studioId) &&
    Boolean(currentStudioId) &&
    data?.studioId !== currentStudioId;

  useEffect(() => {
    if (isStudioMismatch && !isFetching) {
      void refetch();
    }
  }, [currentStudioId, isStudioMismatch, isFetching, refetch]);

  if (!currentStudioId || isLoading || (isStudioMismatch && isFetching)) {
    return (
      <div className='flex justify-center py-16'>
        <Loader2 className='size-10 animate-spin' />
      </div>
    );
  }

  if (isStudioMismatch) {
    return (
      <div className='mx-auto max-w-3xl space-y-4'>
        <Link to='/dashboard' className='flex w-max items-center gap-2'>
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>Setup Checklist</h2>
        </Link>
        <Alert variant='destructive'>
          <AlertTitle>Studio changed</AlertTitle>
          <AlertDescription>
            Checklist data is for a different studio. Refresh to load the
            current studio configuration.
          </AlertDescription>
        </Alert>
        <Button variant='outline' onClick={() => void refetch()}>
          Refresh checklist
        </Button>
      </div>
    );
  }

  if (isError || !data?.summary || !Array.isArray(data.categories)) {
    return (
      <div className='mx-auto max-w-3xl space-y-4'>
        <Link to='/dashboard' className='flex w-max items-center gap-2'>
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>Setup Checklist</h2>
        </Link>
        <Alert variant='destructive'>
          <AlertTitle>Could not load setup checklist</AlertTitle>
          <AlertDescription>
            Something went wrong while checking your studio configuration.
            Please try again.
          </AlertDescription>
        </Alert>
        <Button
          variant='outline'
          disabled={isFetching}
          onClick={() => void refetch()}
        >
          {isFetching ? 'Retrying…' : 'Retry'}
        </Button>
      </div>
    );
  }

  const summary = getVisiblePreflightSummary(data.categories);
  const totalChecks = summary.pass + summary.warn + summary.fail;

  return (
    <div className='mx-auto max-w-4xl space-y-8 pb-12'>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div className='space-y-2'>
          <Link to='/dashboard' className='flex w-max items-center gap-2'>
            <CircleArrowLeft className='size-6' />
            <h2 className='text-2xl font-semibold'>Setup Checklist</h2>
          </Link>
          <p className='text-sm text-muted-foreground'>
            Review required studio configuration before enrollment, billing, and
            parent-facing features go live.
          </p>
          <p className='text-sm font-medium text-foreground'>
            Studio: {data.studioName}
          </p>
        </div>
        <Button
          variant='outline'
          size='sm'
          disabled={isFetching}
          onClick={() => void refetch()}
        >
          <RefreshCw
            className={isFetching ? 'mr-2 size-4 animate-spin' : 'mr-2 size-4'}
          />
          Refresh
        </Button>
      </div>

      <div className='rounded-lg border bg-muted/30 p-4'>
        <p className='text-sm font-medium text-foreground'>
          {summary.pass} of {totalChecks} checks passing
        </p>
        <p className='mt-1 text-sm text-muted-foreground'>
          {summary.fail > 0 ? (
            <span className='font-medium text-red-700'>
              {summary.fail} blocking issue
              {summary.fail === 1 ? '' : 's'}
            </span>
          ) : (
            'No blocking issues'
          )}
          {summary.warn > 0 ? (
            <>
              {' · '}
              <span className='font-medium text-amber-700'>
                {summary.warn} warning{summary.warn === 1 ? '' : 's'}
              </span>
            </>
          ) : null}
        </p>
      </div>

      <div className='space-y-6'>
        {data.categories.map((category) => (
          <PreflightCheckCategory key={category.id} category={category} />
        ))}
      </div>
    </div>
  );
};
